import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Chip,
  Alert,
} from '@mui/material';
import { NotificationPriority } from '../../../types/user';

interface MessageComposerProps {
  title: string;
  message: string;
  priority: NotificationPriority;
  actionLabel?: string;
  actionUrl?: string;
  onTitleChange: (title: string) => void;
  onMessageChange: (message: string) => void;
  onPriorityChange: (priority: NotificationPriority) => void;
  onActionLabelChange: (label: string) => void;
  onActionUrlChange: (url: string) => void;
  disabled?: boolean;
}

/**
 * MessageComposer - Rich text editor for composing broadcast messages
 * Includes title, message, priority, and action button configuration
 */
const MessageComposer: React.FC<MessageComposerProps> = ({
  title,
  message,
  priority,
  actionLabel,
  actionUrl,
  onTitleChange,
  onMessageChange,
  onPriorityChange,
  onActionLabelChange,
  onActionUrlChange,
  disabled = false,
}) => {
  const titleLength = title.length;
  const messageLength = message.length;
  const maxTitleLength = 100;
  const maxMessageLength = 500;

  const getPriorityColor = (p: NotificationPriority): string => {
    switch (p) {
      case NotificationPriority.CRITICAL:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.NORMAL:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (p: NotificationPriority): string => {
    switch (p) {
      case NotificationPriority.CRITICAL:
        return 'Critical - Requires immediate attention';
      case NotificationPriority.HIGH:
        return 'High - Important but not urgent';
      case NotificationPriority.NORMAL:
        return 'Normal - Standard notification';
      case NotificationPriority.LOW:
        return 'Low - Informational only';
      default:
        return 'Normal';
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Compose Message
      </Typography>

      <Grid container spacing={3}>
        {/* Title */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notification Title"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            disabled={disabled}
            required
            error={titleLength > maxTitleLength}
            helperText={`${titleLength}/${maxTitleLength} characters`}
            inputProps={{ maxLength: maxTitleLength }}
          />
        </Grid>

        {/* Message */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Message"
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            disabled={disabled}
            required
            error={messageLength > maxMessageLength}
            helperText={`${messageLength}/${maxMessageLength} characters`}
            inputProps={{ maxLength: maxMessageLength }}
          />
        </Grid>

        {/* Priority */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1 }}>Priority Level</FormLabel>
            <Select
              value={priority}
              onChange={(e) => onPriorityChange(e.target.value as NotificationPriority)}
              disabled={disabled}
            >
              <MenuItem value={NotificationPriority.CRITICAL}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label="Critical"
                    color="error"
                    size="small"
                  />
                  <Typography variant="body2">Requires immediate attention</Typography>
                </Box>
              </MenuItem>
              <MenuItem value={NotificationPriority.HIGH}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label="High"
                    color="warning"
                    size="small"
                  />
                  <Typography variant="body2">Important but not urgent</Typography>
                </Box>
              </MenuItem>
              <MenuItem value={NotificationPriority.NORMAL}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label="Normal"
                    color="info"
                    size="small"
                  />
                  <Typography variant="body2">Standard notification</Typography>
                </Box>
              </MenuItem>
              <MenuItem value={NotificationPriority.LOW}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label="Low"
                    color="default"
                    size="small"
                  />
                  <Typography variant="body2">Informational only</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Alert severity={getPriorityColor(priority) as any} sx={{ mt: 2 }}>
            {getPriorityLabel(priority)}
          </Alert>
        </Grid>

        {/* Action Button (Optional) */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            Action Button (Optional)
          </Typography>
          <TextField
            fullWidth
            label="Button Label"
            value={actionLabel || ''}
            onChange={(e) => onActionLabelChange(e.target.value)}
            disabled={disabled}
            placeholder="e.g., View Details"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Button URL"
            value={actionUrl || ''}
            onChange={(e) => onActionUrlChange(e.target.value)}
            disabled={disabled}
            placeholder="/path/to/page"
            helperText="Relative or absolute URL"
          />
        </Grid>

        {/* Character Count Summary */}
        <Grid item xs={12}>
          <Box display="flex" gap={2}>
            <Chip
              label={`Title: ${titleLength}/${maxTitleLength}`}
              color={titleLength > maxTitleLength ? 'error' : 'default'}
              size="small"
            />
            <Chip
              label={`Message: ${messageLength}/${maxMessageLength}`}
              color={messageLength > maxMessageLength ? 'error' : 'default'}
              size="small"
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MessageComposer;
